import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { Tag, TagKind } from "@redotech/redo-common-model-tags-common/tag";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OpenLikeStatuses } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { PrintKind } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import {
  OMSUser,
  unassignedUser,
} from "@redotech/redo-fulfillment-group-common/oms-user";
import { PrintableDocument } from "@redotech/redo-fulfillment-group-common/print-job";
import type { FulfillmentGroupReassignmentReason } from "@redotech/redo-fulfillment-group-common/reassignment-reason";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { getCreateReturnLink } from "@redotech/redo-merchant-app-common/routing/links";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { showNotification, toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { CheckboxValue } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BinAssignmentModal } from "../common/bin-assignment-modal";
import { CancelOrderModalContext } from "../common/cancel-order/cancel-order-context";
import {
  PinVerificationType,
  usePinVerification,
} from "../common/pin-verification-context";
import { getDeviceLocationId } from "../hooks/use-device-location";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { ManualAutoMergeModal } from "../setting/automations/manual-auto-merge-modal";
import { RerunAutomationsModal } from "../setting/automations/rerun-automations-modal";
import { RerunAutosplitModal } from "../setting/automations/rerun-autosplit-modal";
import { useAllowedActions } from "../utils/use-allowed-actions";
import {
  GroupsAssignedState,
  useFulfillmentGroupPermissions,
} from "../utils/use-assigned-fulfillment-service";
import { AssignToMerchantModal } from "./assign-to-merchant-modal";
import {
  CreatePickSessionFromOrdersModal,
  CreatePickSessionResult,
} from "./create-pick-session-from-orders-modal";
import { MarkAsProblemPickModal } from "./mark-as-problem-pick-modal";
import { MarkUnprintedModal } from "./mark-unprinted-modal";
import {
  MoveToFulfillmentCenterModal,
  ReassignmentLocationOption,
} from "./move-to-fulfillment-center-modal";
import * as ordersCss from "./outbound-labels.module.css";
import {
  RejectedShipmentRow,
  RejectedShipmentsModal,
} from "./rejected-shipments-modal";
import { ReleaseForRepickModal } from "./release-for-repick-modal";
import { ReleaseTotesModal } from "./release-totes-modal";

interface FulfillmentGroupOrderActionsProps {
  selectedRows: ReadonlySet<string>;
  clearSelection: () => void;
  showMergeButton: boolean;
  setMergeModalOpen: (open: boolean) => void;
  fulfillmentGroups: FulfillmentGroupData[];
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  mergeDisabledReason: string;
  setGroupsWaitingForReplacement: (groups: string[]) => void;
  users: OMSUser[];
}

export const FulfillmentGroupOrderActions = ({
  selectedRows,
  clearSelection,
  showMergeButton,
  setMergeModalOpen,
  fulfillmentGroups,
  handleUpdates,
  mergeDisabledReason,
  setGroupsWaitingForReplacement,
  users,
}: FulfillmentGroupOrderActionsProps) => {
  const rpcMerchantClient = useRequiredContext(RedoMerchantRpcClientContext);
  const outboundLabelsRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();
  const openCancelOrderModal = useContext(CancelOrderModalContext);
  const { executeWithPinCheck } = usePinVerification();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownRef, setDropdownRef] = useState<HTMLElement | null>(null);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showAssignUserDropdown, setShowAssignUserDropdown] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState<string>("");
  const [rerunAutomationsModalOpen, setRerunAutomationsModalOpen] =
    useState(false);
  const [rerunAutosplitModalOpen, setRerunAutosplitModalOpen] = useState(false);
  const [binAssignmentModalOpen, setBinAssignmentModalOpen] = useState(false);
  const [markUnprintedModalOpen, setMarkUnprintedModalOpen] = useState(false);
  const [manualAutoMergeModalOpen, setManualAutoMergeModalOpen] =
    useState(false);
  const [releaseTotesModalOpen, setReleaseTotesModalOpen] = useState(false);
  const [releaseForRepickModalOpen, setReleaseForRepickModalOpen] =
    useState(false);
  const [reassignmentService, setReassignmentService] = useState<{
    _id: string;
    name: string;
  } | null>(null);
  const [reassignmentLocations, setReassignmentLocations] = useState<
    readonly ReassignmentLocationOption[]
  >([]);
  const [reassignmentPending, setReassignmentPending] = useState(false);
  const [assignToMerchantOpen, setAssignToMerchantOpen] = useState(false);
  const [assignToMerchantPending, setAssignToMerchantPending] = useState(false);
  const [assignToMerchantLocations, setAssignToMerchantLocations] = useState<
    readonly ReassignmentLocationOption[] | null
  >(null);
  const [markAsProblemPickModalOpen, setMarkAsProblemPickModalOpen] =
    useState(false);
  const [createPickSessionModalOpen, setCreatePickSessionModalOpen] =
    useState(false);
  const [rejectedShipments, setRejectedShipments] = useState<
    RejectedShipmentRow[]
  >([]);
  const [rejectedShipmentsPickSessionId, setRejectedShipmentsPickSessionId] =
    useState<string | null>(null);
  const [rejectedShipmentsModalOpen, setRejectedShipmentsModalOpen] =
    useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState<string>("");
  const searchPlaceholder = "Search Redo shipment tags";

  const [fetchedTags, triggerFetchTags] = useTriggerLoad(async () => {
    try {
      const tagsResult = await rpcMerchantClient.getTeamTags({
        kind: TagKind.FULFILLMENT_GROUP,
      });
      const fetchedTags = [...tagsResult.tags];
      return fetchedTags;
    } catch (error) {
      console.error("Failed to fetch tags:", error);
      return [];
    }
  });

  const availableTags = useMemo(() => {
    return fetchedTags.value || [];
  }, [fetchedTags]);

  const tagItems = useMemo(() => {
    // Transform tags into RedoListItems with badges and menu
    const items = availableTags
      .filter((tag) =>
        tag.name.toLowerCase().includes(tagSearchQuery.trim().toLowerCase()),
      )
      .map((tag) => ({
        id: tag.name,
        text: tag.name,
        badge: { text: tag.name, size: "sm" },
        title: tag.name, // Used for tooltip on hover
        type: "badge" as const,
        value: tag,
      })) as RedoListItem<Tag>[];

    return items;
  }, [availableTags, tagSearchQuery]);

  const [selectedTags, indeterminateTags] = useMemo(() => {
    const indeterminateTagNames: string[] = [];
    const selectedTagNames: string[] = [];
    for (const tag of tagItems) {
      const tagName = tag.value.name;
      const groupsWithTag = fulfillmentGroups.filter((group) =>
        group.tags?.some((t) => t.name === tagName),
      ).length;

      if (groupsWithTag > 0 && groupsWithTag < fulfillmentGroups.length) {
        indeterminateTagNames.push(tagName);
      } else if (groupsWithTag === fulfillmentGroups.length) {
        selectedTagNames.push(tagName);
      }
    }
    return [selectedTagNames, indeterminateTagNames];
  }, [tagItems, fulfillmentGroups]);

  const makeUpdate = useHandler(async (tagName: string, shouldAdd: boolean) => {
    if (shouldAdd) {
      const tagToAdd = availableTags.find((t) => t.name === tagName);
      if (!tagToAdd) {
        toast("Tag not found", { variant: "error" });
        return;
      }
      handleUpdates(
        fulfillmentGroups.map((group) => ({
          fulfillmentGroupId: group._id,
          tags: [...(group.tags || []), tagToAdd],
        })),
      );
    } else {
      handleUpdates(
        fulfillmentGroups.map((group) => ({
          fulfillmentGroupId: group._id,
          tags: group.tags?.filter((t) => t.name !== tagName) || [],
        })),
      );
    }
    amplitude.logEvent("oms-bulk-tag-toggled", {
      direction: shouldAdd ? "added" : "removed",
      fulfillmentGroupCount: fulfillmentGroups.length,
    });
  });

  const userItems = useMemo(() => {
    const uniqueUsers = Array.from(
      new Map(users.map((user) => [user._id, user])).values(),
    );
    return uniqueUsers
      .filter((user) =>
        user.name.toLowerCase().includes(userSearchQuery.trim().toLowerCase()),
      )
      .map(
        (user): RedoListItem<OMSUser> => ({
          id: user._id,
          text: user.name,
          type: "text",
          value: user,
        }),
      );
  }, [users, userSearchQuery]);

  const assignedUserId = useMemo(() => {
    const firstUserId = fulfillmentGroups[0]?.assignedUser?._id;
    if (
      firstUserId &&
      fulfillmentGroups.every(
        (group) => group.assignedUser?._id === firstUserId,
      )
    ) {
      return firstUserId;
    }
    return undefined;
  }, [fulfillmentGroups]);

  const handleAssignUserClick = useHandler(() => {
    setDropdownOpen(false);
    setShowAssignUserDropdown(true);
  });

  const handleUserSelected = useHandler(
    ({ item }: { item: RedoListItem<OMSUser> }) => {
      const user = item.value;
      handleUpdates(
        fulfillmentGroups.map((group) => ({
          fulfillmentGroupId: group._id,
          assignedUser: user,
        })),
      );
      amplitude.logEvent("oms-bulk-assignee-selected", {
        assigneeId: user._id,
        fulfillmentGroupCount: fulfillmentGroups.length,
        unassigned: user._id === unassignedUser._id,
      });
      const orderCountText = `${fulfillmentGroups.length} order${fulfillmentGroups.length === 1 ? "" : "s"}`;
      toast(
        user._id === unassignedUser._id
          ? `Unassigned ${orderCountText}`
          : `Assigned ${orderCountText} to ${user.name}`,
        { variant: "success" },
      );
    },
  );

  const {
    permissionsToEditEveryGroup,
    linkedFulfillmentServices,
    groupsAssignedState,
  } = useFulfillmentGroupPermissions(fulfillmentGroups);

  const { canStartReturn, canCancel } = useAllowedActions(fulfillmentGroups);

  const handleStartReturnClick = useHandler(() => {
    if (fulfillmentGroups.length !== 1) {
      return;
    }
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const orderId = fulfillmentGroups[0].orders[0]?.redoOrderId;
    if (!orderId) {
      console.error(
        "Can't find order ID for fulfillment group to start a return",
      );
      return;
    }
    navigate(getCreateReturnLink(team._id, orderId));
    setDropdownOpen(false);
  });

  const handleCancelOrderClick = useHandler(() => {
    if (!openCancelOrderModal) {
      console.error("Cancel order modal context not available");
      return;
    }
    setDropdownOpen(false);
    executeWithPinCheck(
      () => openCancelOrderModal(fulfillmentGroups),
      { type: PinVerificationType.CancelOrder },
      {
        title: "Cancel Order PIN Verification",
        description:
          "Please enter your PIN to proceed with cancelling the order.",
      },
    );
  });

  // Effect to trigger fetch tags when fetcher data changes
  useEffect(() => {
    triggerFetchTags();
  }, [triggerFetchTags]);

  const onClick = useHandler(() => {
    setDropdownOpen(true);
  });

  const handleTagSelected = useHandler(
    async ({
      item,
      selected,
    }: {
      item: RedoListItem<Tag>;
      selected: CheckboxValue;
      source: "keyboard" | "mouse";
      index: number;
    }) => {
      const tagName = item.value.name;

      // Then perform the actual update in the background
      await makeUpdate(tagName, !!selected);
      setShowTagDropdown(true); // Keep dropdown open when selecting a tag
    },
  );

  const handleRerunAutomationsClick = useHandler(() => {
    setDropdownOpen(false);
    setRerunAutomationsModalOpen(true);
  });

  const handleRerunAutomationsConfirm = useHandler(async () => {
    const fulfillmentGroupIds = fulfillmentGroups.map((group) => group._id);
    amplitude.logEvent("oms-bulk-automations-rerun-submitted", {
      fulfillmentGroupCount: fulfillmentGroupIds.length,
    });
    const { success, error } = await outboundLabelsRpcClient.rerunAutomations({
      fulfillmentGroupIds,
    });
    amplitude.logEvent("oms-bulk-automations-rerun-completed", {
      fulfillmentGroupCount: fulfillmentGroupIds.length,
      succeeded: success,
    });

    if (success) {
      showNotification({
        title: "Rerunning automations...",
        children: (dismiss) => (
          <Text fontSize="sm" textColor="secondary">
            This could take a few minutes to complete.{" "}
            <span
              className={ordersCss.notificationLink}
              onClick={() => {
                dismiss();
                navigate(
                  `/stores/${team._id}/order-management/outbound-labels/dashboard?dashboardTab=order-fulfillment`,
                );
              }}
            >
              Click here to track automation progress
            </span>
          </Text>
        ),
      });
    } else {
      toast(`Failed to trigger automations. ${error}`, { variant: "error" });
    }
  });

  const handleRerunAutomationsClose = useHandler(() => {
    setRerunAutomationsModalOpen(false);
  });

  const handleRerunAutosplitClick = useHandler(() => {
    setDropdownOpen(false);
    setRerunAutosplitModalOpen(true);
  });

  const handleRerunAutosplitConfirm = useHandler(async () => {
    const fulfillmentGroupIds = fulfillmentGroups.map((group) => group._id);
    amplitude.logEvent("oms-bulk-autosplit-rerun-submitted", {
      fulfillmentGroupCount: fulfillmentGroupIds.length,
    });
    const { success, message } = await outboundLabelsRpcClient.rerunAutoSplit({
      fulfillmentGroupIds,
    });
    amplitude.logEvent("oms-bulk-autosplit-rerun-completed", {
      fulfillmentGroupCount: fulfillmentGroupIds.length,
      succeeded: success,
    });

    if (success) {
      toast(message || "Successfully triggered autosplit", {
        variant: "success",
      });
    } else {
      toast(message || "Failed to trigger autosplit", { variant: "error" });
    }
  });

  const handleRerunAutosplitClose = useHandler(() => {
    setRerunAutosplitModalOpen(false);
  });

  const handleBinAssignmentClick = useHandler(() => {
    setDropdownOpen(false);
    setBinAssignmentModalOpen(true);
  });

  const handleMarkUnprintedClick = useHandler(() => {
    setDropdownOpen(false);
    setMarkUnprintedModalOpen(true);
  });

  const handleMarkUnprintedConfirm = useHandler(async () => {
    const fulfillmentGroupIds = fulfillmentGroups.map((group) => group._id);
    amplitude.logEvent("oms-bulk-packing-slips-mark-unprinted-submitted", {
      fulfillmentGroupCount: fulfillmentGroupIds.length,
    });
    try {
      await outboundLabelsRpcClient.markAsPrinted({
        document: PrintableDocument.PACKING_SLIP,
        unprint: true,
        groups: { fulfillmentGroupIds },
      });
      amplitude.logEvent("oms-bulk-packing-slips-marked-unprinted", {
        fulfillmentGroupCount: fulfillmentGroupIds.length,
      });
      toast(
        `Successfully marked ${fulfillmentGroupIds.length} packing slip${fulfillmentGroupIds.length === 1 ? "" : "s"} as unprinted`,
        { variant: "success" },
      );
    } catch (error) {
      amplitude.logEvent("oms-bulk-packing-slips-mark-unprinted-failed", {
        fulfillmentGroupCount: fulfillmentGroupIds.length,
      });
      toast("Failed to mark packing slips as unprinted", { variant: "error" });
      console.error("Failed to mark as unprinted:", error);
    }
  });

  const handleMarkUnprintedClose = useHandler(() => {
    setMarkUnprintedModalOpen(false);
  });

  const handleManualAutoMergeClick = useHandler(() => {
    setDropdownOpen(false);
    setManualAutoMergeModalOpen(true);
  });

  const handleCreatePickSessionClick = useHandler(() => {
    setDropdownOpen(false);
    setCreatePickSessionModalOpen(true);
  });

  const handleCreatePickSessionClose = useHandler(() => {
    setCreatePickSessionModalOpen(false);
  });

  const handleCreatePickSessionResult = useHandler(
    (result: CreatePickSessionResult) => {
      setCreatePickSessionModalOpen(false);

      const hasRejections = result.rejectedRows.length > 0;
      const pickSessionId = result.pickSessionId;
      amplitude.logEvent("oms-bulk-pick-session-created", {
        ...(pickSessionId ? { pickSessionId } : {}),
        rejectedCount: result.rejectedRows.length,
        requestedCount: fulfillmentGroups.length,
      });

      // Success path — show the success toast with a View link. Fires for
      // full success and partial success (with the warning toast below).
      if (pickSessionId) {
        toast("Pick session created", {
          variant: "success",
          duration: Temporal.Duration.from({ seconds: 15 }),
          action: (dismiss) => (
            <RedoButton
              hierarchy="tertiary"
              onClick={() => {
                dismiss();
                navigate(`${routes.pickSessionHref}/${pickSessionId}`);
              }}
              size="sm"
              text="View pick session"
            />
          ),
        });
      }

      // Rejection path — show a warning toast that opens the rejection modal
      // on click. Fires for both partial success and total failure.
      if (hasRejections) {
        setRejectedShipments(result.rejectedRows);
        setRejectedShipmentsPickSessionId(pickSessionId);
        toast(
          `${result.rejectedRows.length} shipment${
            result.rejectedRows.length === 1 ? "" : "s"
          } could not be added`,
          {
            variant: "error",
            duration: Temporal.Duration.from({ seconds: 30 }),
            action: (dismiss) => (
              <RedoButton
                hierarchy="tertiary"
                onClick={() => {
                  dismiss();
                  setRejectedShipmentsModalOpen(true);
                }}
                size="sm"
                text="View details"
              />
            ),
          },
        );
      }
    },
  );

  const handleRejectedShipmentsModalClose = useHandler(() => {
    setRejectedShipmentsModalOpen(false);
  });

  const handleViewPickSessionFromRejections = useHandler(() => {
    if (rejectedShipmentsPickSessionId) {
      navigate(`${routes.pickSessionHref}/${rejectedShipmentsPickSessionId}`);
    }
  });

  const handleManualAutoMergeConfirm = useHandler(async () => {
    const fulfillmentGroupIds = fulfillmentGroups.map((group) => group._id);

    // Mark groups as waiting for merge replacement
    setGroupsWaitingForReplacement(fulfillmentGroupIds);
    amplitude.logEvent("oms-bulk-auto-merge-submitted", {
      fulfillmentGroupCount: fulfillmentGroupIds.length,
    });

    try {
      await outboundLabelsRpcClient.autoMergeSpecifiedGroups({
        fulfillmentGroupIds,
      });
      amplitude.logEvent("oms-bulk-auto-merge-succeeded", {
        fulfillmentGroupCount: fulfillmentGroupIds.length,
      });
      toast("Applying automerge for selected orders", { variant: "success" });
    } catch (error) {
      amplitude.logEvent("oms-bulk-auto-merge-failed", {
        fulfillmentGroupCount: fulfillmentGroupIds.length,
      });
      // Remove from waiting list if failed
      setGroupsWaitingForReplacement([]);
      toast("Failed to apply automerge", { variant: "error" });
      console.error("Failed to apply automerge:", error);
    }
  });

  const handleManualAutoMergeClose = useHandler(() => {
    setManualAutoMergeModalOpen(false);
  });

  const handleReleaseTotesClick = useHandler(() => {
    setDropdownOpen(false);
    setReleaseTotesModalOpen(true);
  });

  const handleReleaseTotesConfirm = useHandler(async () => {
    const toteIds = totesToRelease.map((t) => t.id);
    amplitude.logEvent("oms-bulk-totes-release-submitted", {
      fulfillmentGroupCount: fulfillmentGroups.length,
      toteCount: toteIds.length,
    });
    try {
      const result =
        await outboundLabelsRpcClient.releaseTotesFromFulfillmentGroups({
          toteIds,
        });
      if (result.success) {
        amplitude.logEvent("oms-bulk-totes-released", {
          releasedFulfillmentGroupCount: result.releasedFulfillmentGroupCount,
          releasedToteCount: result.releasedToteCount,
        });
        toast(
          `Released ${result.releasedToteCount} tote${result.releasedToteCount === 1 ? "" : "s"} from ${result.releasedFulfillmentGroupCount} order${result.releasedFulfillmentGroupCount === 1 ? "" : "s"}`,
          { variant: "success" },
        );
      } else {
        amplitude.logEvent("oms-bulk-totes-release-failed", {
          fulfillmentGroupCount: fulfillmentGroups.length,
          toteCount: toteIds.length,
        });
        toast("Failed to release totes", { variant: "error" });
      }
    } catch (error) {
      amplitude.logEvent("oms-bulk-totes-release-failed", {
        fulfillmentGroupCount: fulfillmentGroups.length,
        toteCount: toteIds.length,
      });
      toast("Failed to release totes", { variant: "error" });
      console.error("Failed to release totes:", error);
    }
  });

  const handleReleaseTotesClose = useHandler(() => {
    setReleaseTotesModalOpen(false);
  });

  const handleReleaseForRepickClick = useHandler(() => {
    setDropdownOpen(false);
    setReleaseForRepickModalOpen(true);
  });

  const handleReleaseForRepickConfirm = useHandler(async () => {
    let releasedCount = 0;
    for (const group of fulfillmentGroups) {
      try {
        const result = await outboundLabelsRpcClient.cancelPicking({
          fulfillmentGroupId: group._id,
        });
        if (result.success) {
          releasedCount++;
        }
      } catch (error) {
        console.error("Failed to release for repick:", error);
      }
    }
    amplitude.logEvent("oms-bulk-repick-release-completed", {
      failedCount: fulfillmentGroups.length - releasedCount,
      fulfillmentGroupCount: fulfillmentGroups.length,
      releasedCount,
    });
    if (releasedCount > 0) {
      toast(
        `Released ${releasedCount} order${releasedCount === 1 ? "" : "s"} for repick`,
        { variant: "success" },
      );
    } else {
      toast("Failed to release for repick", { variant: "error" });
    }
  });

  const handleReleaseForRepickClose = useHandler(() => {
    setReleaseForRepickModalOpen(false);
  });

  const handleMarkAsProblemPickClick = useHandler(() => {
    setDropdownOpen(false);
    setMarkAsProblemPickModalOpen(true);
  });

  const handleMarkAsProblemPickConfirm = useHandler(async () => {
    const fulfillmentGroupIds = fulfillmentGroups.map((group) => group._id);
    amplitude.logEvent("oms-bulk-problem-pick-submitted", {
      fulfillmentGroupCount: fulfillmentGroupIds.length,
    });
    try {
      const result =
        await outboundLabelsRpcClient.markFulfillmentGroupsAsProblemPick({
          fulfillmentGroupIds,
          crdbLocationId: getDeviceLocationId() ?? undefined,
        });
      if (result.success) {
        amplitude.logEvent("oms-bulk-problem-pick-completed", {
          failedCount: fulfillmentGroupIds.length - result.successCount,
          fulfillmentGroupCount: fulfillmentGroupIds.length,
          succeededCount: result.successCount,
        });
        toast(
          `Marked ${result.successCount} order${result.successCount === 1 ? "" : "s"} as problem pick`,
          { variant: "success" },
        );
        setMarkAsProblemPickModalOpen(false);
      } else {
        amplitude.logEvent("oms-bulk-problem-pick-completed", {
          failedCount: fulfillmentGroupIds.length,
          fulfillmentGroupCount: fulfillmentGroupIds.length,
          succeededCount: 0,
        });
        toast(result.message || "Failed to mark as problem pick", {
          variant: "error",
        });
      }
    } catch (error) {
      amplitude.logEvent("oms-bulk-problem-pick-completed", {
        failedCount: fulfillmentGroupIds.length,
        fulfillmentGroupCount: fulfillmentGroupIds.length,
        succeededCount: 0,
      });
      toast("Failed to mark as problem pick", { variant: "error" });
      console.error("Failed to mark as problem pick:", error);
    }
  });

  const handleMarkAsProblemPickClose = useHandler(() => {
    setMarkAsProblemPickModalOpen(false);
  });

  const handleAssignToTeam = useHandler(() => {
    setDropdownOpen(false);
    handleUpdates(
      fulfillmentGroups.map((g) => ({
        fulfillmentGroupId: g._id,
        assignedFulfillmentService: g.teamInfo,
      })),
    );
    toast(
      `Assigned ${fulfillmentGroups.length} order${fulfillmentGroups.length === 1 ? "" : "s"} to my store`,
      { variant: "success" },
    );
  });

  const anyGroupsInPickSession = useMemo(() => {
    return fulfillmentGroups.some(
      (group) => !!group.pickStatus || !!group.pickSessionId,
    );
  }, [fulfillmentGroups]);

  const allGroupsOpenLike = useMemo(() => {
    return fulfillmentGroups.every((group) =>
      OpenLikeStatuses.includes(group.status),
    );
  }, [fulfillmentGroups]);

  const assignToMerchantDisabledReason =
    fulfillmentGroups.length > 1
      ? "Select a single order to assign to the Merchant."
      : anyGroupsInPickSession
        ? "Release orders from pick sessions before assigning to the Merchant."
        : !allGroupsOpenLike
          ? "Only open orders can be assigned to the Merchant."
          : fulfillmentGroups.some((group) => !group.teamInfo)
            ? "Selected orders are missing merchant information."
            : "";

  const assignToMerchantGroup =
    fulfillmentGroups.length === 1 ? (fulfillmentGroups[0] ?? null) : null;

  const handleAssignToMerchantClick = useHandler(() => {
    setDropdownOpen(false);
    if (!assignToMerchantGroup) {
      return;
    }
    amplitude.logEvent("oms-assign-to-merchant-opened", {});
    setAssignToMerchantLocations(null);
    setAssignToMerchantOpen(true);
    void outboundLabelsRpcClient
      .getMerchantReassignmentLocations({
        fulfillmentGroupId: assignToMerchantGroup._id,
      })
      .then(({ locations }) => setAssignToMerchantLocations(locations))
      .catch((error) => {
        console.error("Failed to load merchant locations:", error);
        setAssignToMerchantLocations([]);
      });
  });

  const handleAssignToMerchantConfirm = useHandler(
    async (input: {
      reason: FulfillmentGroupReassignmentReason;
      note: string;
      externalLocationId?: string;
    }) => {
      const group = assignToMerchantGroup;
      if (!group) {
        return;
      }
      setAssignToMerchantPending(true);
      amplitude.logEvent("oms-assign-to-merchant-submitted", {
        reason: input.reason,
      });
      try {
        await outboundLabelsRpcClient.assignFulfillmentGroupToMerchant({
          fulfillmentGroupId: group._id,
          externalLocationId: input.externalLocationId,
          reason: input.reason,
          note: input.note || undefined,
        });
        amplitude.logEvent("oms-assign-to-merchant-completed", {
          reason: input.reason,
          succeeded: true,
        });
        toast(
          `Assigned ${group.orders?.[0]?.name ?? "order"} to ${group.teamInfo?.name ?? "the Merchant"}`,
          { variant: "success" },
        );
        clearSelection();
        setAssignToMerchantOpen(false);
      } catch (error) {
        amplitude.logEvent("oms-assign-to-merchant-completed", {
          reason: input.reason,
          succeeded: false,
        });
        console.error("Failed to assign order to the Merchant:", error);
        toast("Failed to assign order to the Merchant", { variant: "error" });
      } finally {
        setAssignToMerchantPending(false);
      }
    },
  );

  const handleAssignToFulfillmentService = useHandler(
    async (service: { _id: string; name: string }) => {
      setDropdownOpen(false);
      if (fulfillmentGroups.length === 0) {
        return;
      }

      setReassignmentPending(true);
      try {
        const { locations } =
          await outboundLabelsRpcClient.getReassignmentLocations({
            fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
            fulfillmentServiceId: service._id,
          });
        if (locations.length === 0) {
          handleUpdates(
            fulfillmentGroups.map((g) => ({
              fulfillmentGroupId: g._id,
              assignedFulfillmentService: service,
            })),
          );
          toast(
            `Assigned ${fulfillmentGroups.length} order${fulfillmentGroups.length === 1 ? "" : "s"} to ${service.name}`,
            { variant: "success" },
          );
          return;
        }
        setReassignmentLocations(locations);
        setReassignmentService(service);
      } catch (error) {
        console.error("Failed to load reassignment locations:", error);
        toast("Couldn't load destination locations", { variant: "error" });
      } finally {
        setReassignmentPending(false);
      }
    },
  );

  const handleConfirmReassignment = useHandler(
    async (externalLocationId: string) => {
      if (fulfillmentGroups.length === 0 || !reassignmentService) {
        return;
      }
      const service = reassignmentService;
      const groups = [...fulfillmentGroups];
      setReassignmentService(null);
      amplitude.logEvent("oms-bulk-reassignment-submitted", {
        fulfillmentGroupCount: groups.length,
        fulfillmentServiceId: service._id,
        locationId: externalLocationId,
      });
      try {
        const { successCount, totalCount } =
          await outboundLabelsRpcClient.reassignAndMoveFulfillmentGroup({
            fulfillmentGroupIds: groups.map((g) => g._id),
            fulfillmentServiceId: service._id,
            externalLocationId,
          });
        amplitude.logEvent("oms-bulk-reassignment-completed", {
          failedCount: totalCount - successCount,
          fulfillmentGroupCount: totalCount,
          fulfillmentServiceId: service._id,
          succeededCount: successCount,
        });
        const noun = `order${totalCount === 1 ? "" : "s"}`;
        if (successCount === totalCount) {
          toast(`Moving ${totalCount} ${noun} to ${service.name}`, {
            variant: "success",
          });
          clearSelection();
        } else if (successCount > 0) {
          toast(
            `Moved ${successCount} of ${totalCount} ${noun}; ${totalCount - successCount} failed`,
            { variant: "warning" },
          );
        } else {
          toast(`Failed to move ${noun}`, { variant: "error" });
        }
      } catch (error) {
        amplitude.logEvent("oms-bulk-reassignment-completed", {
          failedCount: groups.length,
          fulfillmentGroupCount: groups.length,
          fulfillmentServiceId: service._id,
          succeededCount: 0,
        });
        console.error("Failed to move fulfillment groups:", error);
        toast("Failed to move orders", { variant: "error" });
      }
    },
  );

  // Check if all selected groups are unfulfilled (OpenLikeStatus)
  const allGroupsUnfulfilled = useMemo(() => {
    return fulfillmentGroups.every((group) =>
      OpenLikeStatuses.includes(group.status),
    );
  }, [fulfillmentGroups]);

  // Check if any selected groups have a pick status (are in a pick session)
  const anyGroupsHavePickStatus = useMemo(() => {
    return fulfillmentGroups.some((group) => group.pickStatus);
  }, [fulfillmentGroups]);

  // Check if all selected groups have packing slips printed
  const allGroupsHavePackingSlipPrinted = useMemo(() => {
    return fulfillmentGroups.every(
      (group) => group.printStatus?.[PrintKind.PACKING_SLIP],
    );
  }, [fulfillmentGroups]);

  // Get unique totes from selected fulfillment groups
  const totesToRelease = useMemo(() => {
    const toteMap = new Map<string, { id: string; name: string }>();
    for (const group of fulfillmentGroups) {
      if (group.assignedTotes) {
        for (const tote of group.assignedTotes) {
          toteMap.set(tote._id, { id: tote._id, name: tote.name });
        }
      }
    }
    return Array.from(toteMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [fulfillmentGroups]);

  const toteNamesToRelease = useMemo(
    () => totesToRelease.map((t) => t.name),
    [totesToRelease],
  );

  const dropdownItems: RedoCommandMenuItem[] = useMemo(() => {
    const items: RedoCommandMenuItem[] = [
      {
        text: "Merge orders",
        onClick: () => setMergeModalOpen(true),
        disabled: !showMergeButton,
        disabledTooltip: mergeDisabledReason,
      },
    ];

    if (team?.settings?.outboundLabels?.wms?.enabled) {
      items.push({
        text: "Create pick session",
        onClick: handleCreatePickSessionClick,
        disabled: false,
      });
    }

    items.push(
      {
        text: "Change or add Redo shipment tags",
        onClick: () => {
          setDropdownOpen(false);
          setShowTagDropdown(true);
          triggerFetchTags();
        },
        disabled: false,
      },
      { text: "Assign user", onClick: handleAssignUserClick, disabled: false },
      {
        text: "Rerun automations",
        onClick: handleRerunAutomationsClick,
        disabled: !allGroupsUnfulfilled,
      },
      {
        text: "Rerun autosplit",
        onClick: handleRerunAutosplitClick,
        disabled: !allGroupsUnfulfilled,
        disabledTooltip: !allGroupsUnfulfilled
          ? "Can only rerun autosplit on unfulfilled orders"
          : undefined,
      },
    );

    items.push({
      text: "Apply automerge",
      onClick: handleManualAutoMergeClick,
      disabled: !allGroupsUnfulfilled,
      disabledTooltip: !allGroupsUnfulfilled
        ? "Can only apply automerge on unfulfilled orders"
        : undefined,
    });

    if (team?.settings?.outboundLabels?.canUpdateBinsFromTable) {
      items.push({
        text: "Assign bins",
        onClick: handleBinAssignmentClick,
        disabled: false,
      });
    }

    items.push({
      text: "Mark unprinted",
      onClick: handleMarkUnprintedClick,
      disabled: !allGroupsHavePackingSlipPrinted,
      disabledTooltip: !allGroupsHavePackingSlipPrinted
        ? "Can only mark orders as unprinted if all selected orders have packing slips printed"
        : undefined,
    });

    if (canStartReturn) {
      items.push({
        text: "Start a return",
        onClick: handleStartReturnClick,
        disabled: false,
      });
    }

    if (canCancel) {
      items.push({
        text: "Cancel order",
        onClick: handleCancelOrderClick,
        disabled: false,
      });
    }

    if (toteNamesToRelease.length > 0) {
      items.push({
        text: "Release totes",
        onClick: handleReleaseTotesClick,
        disabled: false,
      });
    }

    if (anyGroupsHavePickStatus) {
      items.push({
        text: "Release for repick",
        onClick: handleReleaseForRepickClick,
        disabled: false,
      });
    }

    // Mark as problem pick - available for unfulfilled orders
    if (allGroupsUnfulfilled) {
      items.push({
        text: "Mark as problem pick",
        onClick: handleMarkAsProblemPickClick,
        disabled: false,
      });
    }

    // Reassign to fulfillment service - available when assigned to my store
    if (
      groupsAssignedState === GroupsAssignedState.MyStore &&
      linkedFulfillmentServices.length > 0
    ) {
      for (const service of linkedFulfillmentServices) {
        items.push({
          text: `Assign to ${service.name}`,
          onClick: () => handleAssignToFulfillmentService(service),
          disabled: reassignmentPending,
        });
      }
    }

    // Reassign to my store - available when assigned to a fulfillment service
    if (
      groupsAssignedState === GroupsAssignedState.DifferentFulfillmentService
    ) {
      items.push({
        text: "Assign to my store",
        onClick: handleAssignToTeam,
        disabled: false,
      });
    }

    if (groupsAssignedState === GroupsAssignedState.MyFulfillmentService) {
      items.push({
        text: "Assign to Merchant",
        onClick: handleAssignToMerchantClick,
        disabled: !!assignToMerchantDisabledReason,
        disabledTooltip: assignToMerchantDisabledReason || undefined,
      });
    }

    return items;
  }, [
    showMergeButton,
    mergeDisabledReason,
    setMergeModalOpen,
    setDropdownOpen,
    setShowTagDropdown,
    triggerFetchTags,
    handleAssignUserClick,
    handleRerunAutomationsClick,
    handleRerunAutosplitClick,
    allGroupsUnfulfilled,
    team?.settings?.outboundLabels?.wms?.enabled,
    team?.settings?.outboundLabels?.canUpdateBinsFromTable,
    handleBinAssignmentClick,
    handleMarkUnprintedClick,
    handleManualAutoMergeClick,
    allGroupsHavePackingSlipPrinted,
    canStartReturn,
    handleStartReturnClick,
    canCancel,
    handleCancelOrderClick,
    toteNamesToRelease,
    handleReleaseTotesClick,
    anyGroupsHavePickStatus,
    handleReleaseForRepickClick,
    handleMarkAsProblemPickClick,
    groupsAssignedState,
    linkedFulfillmentServices,
    handleAssignToFulfillmentService,
    handleAssignToTeam,
    handleCreatePickSessionClick,
    reassignmentPending,
    handleAssignToMerchantClick,
    assignToMerchantDisabledReason,
  ]);

  if (!selectedRows.size) {
    return null;
  }

  return (
    <Flex>
      <RedoButton
        buttonClassName={ordersCss.clearButtonMultiSelected}
        hierarchy="secondary"
        IconTrailing={XIcon}
        onClick={clearSelection}
        size="sm"
        text={
          selectedRows.size === 1
            ? "1 order selected"
            : `${selectedRows.size} orders selected`
        }
      />
      <RedoButton
        buttonClassName={ordersCss.clearButtonSingleSelected}
        disabled={dropdownItems.length === 0 || !permissionsToEditEveryGroup}
        hierarchy="secondary"
        IconTrailing={ChevronDownIcon}
        onClick={onClick}
        ref={setDropdownRef}
        size="sm"
        text="Order actions"
        theme="normal"
      />
      <RedoCommandMenu
        anchor={dropdownRef}
        fitToAnchor={false}
        items={dropdownItems}
        open={dropdownOpen && !showTagDropdown && !showAssignUserDropdown}
        placement="bottom-end"
        setOpen={setDropdownOpen}
        size="sm"
      />
      <RedoListDropdown
        dropdownAnchor={dropdownRef}
        dropdownOpen={showTagDropdown}
        dropdownPlacement="bottom-end"
        fitToAnchor={false}
        indeterminateItems={indeterminateTags}
        items={tagItems}
        itemSelected={handleTagSelected}
        preventCloseOnAnchorClick
        searchPlaceholder={searchPlaceholder}
        searchString={tagSearchQuery}
        selectedItems={selectedTags}
        selectionVariant="checkbox"
        setDropdownOpen={setShowTagDropdown}
        setSearchString={setTagSearchQuery}
        size="xs"
      />
      <RedoListDropdown
        dropdownAnchor={dropdownRef}
        dropdownOpen={showAssignUserDropdown}
        dropdownPlacement="bottom-end"
        fitToAnchor={false}
        items={userItems}
        itemSelected={handleUserSelected}
        searchPlaceholder="Search users..."
        searchString={userSearchQuery}
        selectedItems={assignedUserId}
        setDropdownOpen={setShowAssignUserDropdown}
        setSearchString={setUserSearchQuery}
        size="xs"
      />

      <RerunAutomationsModal
        isAllUnfulfilled={false}
        isOpen={rerunAutomationsModalOpen}
        numAutomations={undefined} // We don't have this information here
        numUnfulfilledGroups={fulfillmentGroups.length}
        onClose={handleRerunAutomationsClose}
        onConfirm={handleRerunAutomationsConfirm}
      />

      <RerunAutosplitModal
        isOpen={rerunAutosplitModalOpen}
        numSelectedGroups={fulfillmentGroups.length}
        onClose={handleRerunAutosplitClose}
        onConfirm={handleRerunAutosplitConfirm}
      />

      <BinAssignmentModal
        groups={fulfillmentGroups}
        open={binAssignmentModalOpen}
        setOpen={setBinAssignmentModalOpen}
      />

      <MarkUnprintedModal
        isOpen={markUnprintedModalOpen}
        numSelectedGroups={fulfillmentGroups.length}
        onClose={handleMarkUnprintedClose}
        onConfirm={handleMarkUnprintedConfirm}
      />

      <ManualAutoMergeModal
        isOpen={manualAutoMergeModalOpen}
        numSelectedGroups={fulfillmentGroups.length}
        onClose={handleManualAutoMergeClose}
        onConfirm={handleManualAutoMergeConfirm}
      />

      <ReleaseTotesModal
        isOpen={releaseTotesModalOpen}
        onClose={handleReleaseTotesClose}
        onConfirm={handleReleaseTotesConfirm}
        toteNames={toteNamesToRelease}
      />

      <ReleaseForRepickModal
        isOpen={releaseForRepickModalOpen}
        numSelectedGroups={fulfillmentGroups.length}
        onClose={handleReleaseForRepickClose}
        onConfirm={handleReleaseForRepickConfirm}
      />

      <MarkAsProblemPickModal
        isOpen={markAsProblemPickModalOpen}
        numSelectedGroups={fulfillmentGroups.length}
        onClose={handleMarkAsProblemPickClose}
        onConfirm={handleMarkAsProblemPickConfirm}
      />

      <CreatePickSessionFromOrdersModal
        fulfillmentGroups={fulfillmentGroups}
        isOpen={createPickSessionModalOpen}
        onClose={handleCreatePickSessionClose}
        onResult={handleCreatePickSessionResult}
        users={users}
      />

      <RejectedShipmentsModal
        isOpen={rejectedShipmentsModalOpen}
        onClose={handleRejectedShipmentsModalClose}
        onViewPickSession={
          rejectedShipmentsPickSessionId
            ? handleViewPickSessionFromRejections
            : undefined
        }
        rows={rejectedShipments}
      />

      {reassignmentService && (
        <MoveToFulfillmentCenterModal
          isOpen={!!reassignmentService}
          locations={reassignmentLocations}
          onClose={() => setReassignmentService(null)}
          onConfirm={handleConfirmReassignment}
          orderName={
            fulfillmentGroups.length === 1
              ? (fulfillmentGroups[0]?.orders[0]?.name ?? "Order")
              : `${fulfillmentGroups.length} orders`
          }
          serviceName={reassignmentService.name}
          totalUnits={
            reassignmentLocations[0]?.items.reduce(
              (sum, i) => sum + i.needed,
              0,
            ) ?? 0
          }
        />
      )}

      <AssignToMerchantModal
        fulfillmentGroup={assignToMerchantGroup}
        isOpen={assignToMerchantOpen}
        locations={assignToMerchantLocations}
        onClose={() => setAssignToMerchantOpen(false)}
        onConfirm={handleAssignToMerchantConfirm}
        pending={assignToMerchantPending}
        warehouseName={team.name}
      />
    </Flex>
  );
};
